/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {dlsColor} from '../../../theme/theme';

// this bar's own fill duration IS the refresh cadence - both /tracking and
// /tracking2 derive their actual refresh timing from this component's cycle
// (via onCycleComplete/onAnimationEnd), rather than a separate interval ...
export const ASSET_UPDATE_INTERVAL_SECONDS = 12;

interface AssetUpdateProgressBarProps {
  isPlaying: boolean;
  cycleKey: number;
  durationSeconds?: number;
  // true while the auto-refresh request for this cycle is in flight - the
  // fill is already paused via isPlaying=false during this window, but this
  // additionally overlays a pulsing indicator so waiting for data reads as
  // "actively fetching" rather than just a frozen bar ...
  isFetching?: boolean;
  // fired once the fill genuinely finishes its run (native `animationend`
  // on the fill element) - this is the sole trigger the caller uses to
  // start the next refresh, so pausing partway through (which suspends the
  // CSS animation, not just its visual position) correctly delays it, and
  // resuming continues counting only the remaining time rather than firing
  // early or restarting ...
  onCycleComplete?: () => void;
}

interface StyleProps {
  isPlaying: boolean;
  durationSeconds: number;
}

const useStyles = makeStyles(() => ({
  container : {
    display       : 'flex',
    flexDirection : 'column',
    gap           : '4px',
    minWidth      : '74px'
  },
  label : {
    fontFamily    : 'Roboto',
    fontWeight    : 700,
    fontSize      : '10px',
    lineHeight    : '14px',
    letterSpacing : '1px',
    textTransform : 'uppercase',
    color         : dlsColor('grey3'),
    whiteSpace    : 'nowrap'
  },
  track : {
    position        : 'relative',
    width           : '100%',
    height          : '4px',
    borderRadius    : '4px',
    backgroundColor : dlsColor('paleBlue'),
    overflow        : 'hidden'
  },
  // longhand properties (not the `animation` shorthand) so the dynamic,
  // per-render duration doesn't have to share a value with the `$fill`
  // local keyframe reference - `animationName` stays a plain string, which
  // is what JSS's local-scope substitution actually needs to resolve it.
  // A single pass per mount (no `infinite`) - the caller remounts this
  // element (via `key={cycleKey}`) exactly once per real refresh cycle, so
  // the fill is always tied to actual elapsed time within that cycle.
  // Looping the CSS animation independently of that remount would give it
  // its own, slightly different period, so the two would drift apart and
  // the bar would visibly restart before finishing - `forwards` holds it at
  // 100% if a remount is ever late ...
  // the fill is full width and slides in from the left (the track clips the
  // rest), animated with `transform` rather than `width` - a transform runs
  // on the compositor, so the bar keeps moving smoothly while the page is
  // busy with an update, instead of stalling and jumping with it ...
  fill : {
    position                : 'absolute',
    left                    : 0,
    top                     : 0,
    width                   : '100%',
    height                  : '100%',
    willChange              : 'transform',
    borderRadius            : '4px',
    backgroundColor         : dlsColor('darkTeal'),
    animationName           : '$fill',
    animationDuration       : ({durationSeconds}: StyleProps) => `${durationSeconds}s`,
    animationTimingFunction : 'linear',
    animationFillMode       : 'forwards',
    animationPlayState      : ({isPlaying}: StyleProps) => isPlaying ? 'running' : 'paused'
  },
  '@keyframes fill' : {
    from : {
      transform : 'translateX(-100%)'
    },
    to : {
      transform : 'translateX(0%)'
    }
  },
  // fills the whole track under the sweep while fetching - the fill is only
  // full at the end of a cycle, and a sort, filter or first load can start a
  // fetch part-way through one, where the pale sweep would vanish against
  // the track's own pale background beyond the fill ...
  fetchingBackdrop : {
    position        : 'absolute',
    left            : 0,
    top             : 0,
    width           : '100%',
    height          : '100%',
    borderRadius    : '4px',
    backgroundColor : dlsColor('darkTeal')
  },
  // overlays the (paused, fully-filled) fill while the cycle's fetch is in
  // flight - the fill is at 100% width in `darkTeal` at this point (the
  // cycle just completed), so the sweeping segment uses `paleBlue` instead
  // of reusing `darkTeal` - the same color on top of itself would be
  // invisible against the bar it's meant to indicate over ...
  fetchingIndicator : {
    position                : 'absolute',
    left                    : 0,
    top                     : 0,
    willChange              : 'transform',
    width                   : '40%',
    height                  : '100%',
    borderRadius            : '4px',
    backgroundColor         : dlsColor('paleBlue'),
    animationName           : '$sweep',
    animationDuration       : '1.2s',
    animationIterationCount : 'infinite',
    animationTimingFunction : 'ease-in-out'
  },
  // the 40%-wide segment travels from fully left of the track (-100% of
  // its own width) to fully right of it (250%), as `left` -40% to 100% did ...
  '@keyframes sweep' : {
    '0%' : {
      transform : 'translateX(-100%)'
    },
    '100%' : {
      transform : 'translateX(250%)'
    }
  }
}));

/**
 * AssetUpdateProgressBar renders the "Data Updates Every N Sec." label and a
 * bar that fills over `durationSeconds`, pausable via `isPlaying` and
 * overlaid with a sweeping indeterminate segment while `isFetching`. It is
 * purely presentational - the caller's `onCycleComplete` (fired once per
 * completed fill) decides what a completed cycle means and owns the actual
 * refresh cadence; see `useTracking2RefreshCycle`/`useLegacyRefreshCycle` in
 * `@ion/ion-suite` for that driving logic.
 *
 * ```typescript
 *
 *   import {AssetUpdateProgressBar} from
 *     '@ion/dls/src/components/sidepanel/current/AssetUpdateProgressBar';
 *   ...
 *
 *   <AssetUpdateProgressBar isPlaying={isAssetUpdatesEnabled}
 *     isFetching={isRefreshFetching}
 *     onCycleComplete={onProgressCycleComplete}
 *     cycleKey={updateCycleKey} />
 *
 * ```
 *
 */
export const AssetUpdateProgressBar = ({
  isPlaying,
  cycleKey,
  durationSeconds = ASSET_UPDATE_INTERVAL_SECONDS,
  isFetching = false,
  onCycleComplete
}: AssetUpdateProgressBarProps) => {
  const classes = useStyles({isPlaying, durationSeconds});
  return (
    <div className={classes.container}>
      <span className={classes.label}>
        {translate('common:liveUpdates.dataUpdates.label')}
        <br />
        {translate('common:liveUpdates.dataUpdates.interval', {seconds: durationSeconds})}
      </span>
      <div className={classes.track}>
        <div key={cycleKey} className={classes.fill} onAnimationEnd={onCycleComplete} />
        {isFetching && <div className={classes.fetchingBackdrop} />}
        {isFetching && <div className={classes.fetchingIndicator} />}
      </div>
    </div>
  );
};

export default AssetUpdateProgressBar;
